import React, { useState } from "react";
import Card from "components/card";
import Dropdown from "components/dropdown";
import { MdExpandMore, MdExpandLess, MdAdd, MdSearch, MdShoppingCart, MdEdit, MdDelete, MdClose, MdKeyboardArrowDown, MdPerson, MdEmail, MdPhone, MdLocationOn, MdSave, MdArrowBack } from "react-icons/md";
import { 
  FaPlane, 
  FaHotel, 
  FaTrain, 
  FaCar, 
  FaPassport,
  FaUser,
  FaCalendarAlt,
  FaClock,
  FaMapMarkerAlt
} from "react-icons/fa";
import { HiSparkles, HiLightningBolt } from "react-icons/hi";
import FlightModal from "./components/FlightModal";
import HotelModal from "./components/HotelModal";
import RailwayModal from "./components/RailwayModal";
import CarModal from "./components/CarModal";
import VisaModal from "./components/VisaModal";
import DeleteModal from "./components/DeleteModal";
import FlightSearchModal from "./components/FlightSearchModal";
import FlightResultsModal from "./components/FlightResultsModal";
import VehicleSearchModal from "./components/VehicleSearchModal";
import VehicleResultsModal from "./components/VehicleResultsModal";
import TrainSearchModal from "./components/TrainSearchModal";
import TrainResultsModal from "./components/TrainResultsModal";
import HotelSearchModal from "./components/HotelSearchModal";
import HotelResultsModal from "./components/HotelResultsModal";

const BookingDetails = () => {
  const [expandedCard, setExpandedCard] = useState(null);
  const [bookingData, setBookingData] = useState({
    flight: [],
    hotel: [],
    railway: [],
    car: [],
    visa: []
  });
  const [searchModals, setSearchModals] = useState({
    flight: false,
    hotel: false,
    railway: false,
    car: false,
    visa: false
  });
  const [resultsModals, setResultsModals] = useState({
    flight: false,
    hotel: false,
    railway: false,
    car: false,
    visa: false
  });
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [modals, setModals] = useState({
    flight: false,
    hotel: false,
    railway: false,
    car: false,
    visa: false
  });
  const [editingItem, setEditingItem] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, cardId: null, itemId: null, itemName: '' });
  const [showCart, setShowCart] = useState(false);
  const [nextId, setNextId] = useState({
    flight: 4,
    hotel: 4,
    railway: 4,
    car: 4,
    visa: 4
  });
  const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', message: '' });
  const [editingCustomer, setEditingCustomer] = useState(!new URLSearchParams(window.location.search).get('carteId'));
  const [staticItems, setStaticItems] = useState({
    flight: [],
    hotel: [],
    railway: [],
    car: [],
    visa: []
  });

  // Load carte data if carteId is provided in URL
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const carteId = urlParams.get('carteId');
    
    if (carteId) {
      const savedCartes = JSON.parse(localStorage.getItem('savedCartes') || '[]');
      const carteIndex = parseInt(carteId) - 1;
      
      if (savedCartes[carteIndex]) {
        const carteData = savedCartes[carteIndex];
        
        // Load customer data
        setCustomerData(carteData.customer);
        setSelectedCustomer(carteData.customer.name);
        setCardsEnabled(true);
        
        // Load booking data
        setStaticItems({
          flight: carteData.bookings.flight || [],
          hotel: carteData.bookings.hotel || [],
          railway: carteData.bookings.railway || [],
          car: carteData.bookings.car || [],
          visa: carteData.bookings.visa || []
        });
      }
    }
  }, []);

  const toggleCard = (cardId) => {
    setExpandedCard(prev => prev === cardId ? null : cardId);
  };

  const openSearchModal = (cardId) => {
    setSearchModals(prev => ({ ...prev, [cardId]: true }));
  };

  const closeSearchModal = (cardId) => {
    setSearchModals(prev => ({ ...prev, [cardId]: false }));
  };

  const openResultsModal = (cardId) => {
    setResultsModals(prev => ({ ...prev, [cardId]: true }));
  };

  const closeResultsModal = (cardId) => {
    setResultsModals(prev => ({ ...prev, [cardId]: false }));
  };

  const addItem = (cardId, item) => {
    if (editingItem) {
      // Update existing item
      setBookingData(prev => ({
        ...prev,
        [cardId]: prev[cardId].map(existingItem => 
          existingItem.id === editingItem.id ? { ...item, id: editingItem.id } : existingItem
        )
      }));
      setEditingItem(null);
    } else {
      // Add new item
      const newItem = { ...item, id: nextId[cardId] };
      setBookingData(prev => ({
        ...prev,
        [cardId]: [...prev[cardId], newItem]
      }));
      setNextId(prev => ({ ...prev, [cardId]: prev[cardId] + 1 }));
    }
    
    // Trigger auto-save flag
    setAutoSaving(true);
  };

  const openDeleteModal = (cardId, itemId, itemName) => {
    setDeleteModal({ isOpen: true, cardId, itemId, itemName });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, cardId: null, itemId: null, itemName: '' });
  };

  const confirmDelete = () => {
    const { cardId, itemId } = deleteModal;
    
    if (itemId <= 3) {
      // Remove from static items
      setStaticItems(prev => ({
        ...prev,
        [cardId]: prev[cardId].filter(item => item.id !== itemId)
      }));
    } else {
      // Remove from dynamic items
      setBookingData(prev => ({
        ...prev,
        [cardId]: prev[cardId].filter(item => item.id !== itemId)
      }));
    }
    
    closeDeleteModal();
  };

  const handleSearch = (cardId, searchData) => {
    setSearchResults(searchData);
    // Show results immediately
    openResultsModal(cardId);
  };

  const handleSelectResult = (cardId, selectedItem) => {
    // Add selected item to booking data
    const newItem = { ...selectedItem, id: nextId[cardId] };
    setBookingData(prev => ({
      ...prev,
      [cardId]: [...prev[cardId], newItem]
    }));
    setNextId(prev => ({ ...prev, [cardId]: prev[cardId] + 1 }));
    
    // Trigger auto-save
    setAutoSaving(true);
    
    closeResultsModal(cardId);
  };

  const saveToLocalStorage = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const carteId = urlParams.get('carteId');
    
    const carteData = {
      customer: customerData,
      bookings: {
        flight: [...bookingData.flight, ...staticItems.flight],
        hotel: [...bookingData.hotel, ...staticItems.hotel],
        railway: [...bookingData.railway, ...staticItems.railway],
        car: [...bookingData.car, ...staticItems.car],
        visa: [...bookingData.visa, ...staticItems.visa]
      },
      timestamp: new Date().toISOString()
    };
    
    const existingCartes = JSON.parse(localStorage.getItem('savedCartes') || '[]');
    
    if (carteId) {
      // Update existing carte
      const carteIndex = parseInt(carteId) - 1;
      if (carteIndex >= 0 && carteIndex < existingCartes.length) {
        existingCartes[carteIndex] = carteData;
      }
    } else {
      // Check if carte with same customer already exists
      const existingIndex = existingCartes.findIndex(carte => 
        carte.customer.id === customerData.id
      );
      
      if (existingIndex !== -1) {
        // Update existing carte
        existingCartes[existingIndex] = carteData;
      } else {
        // Add new carte
        existingCartes.unshift(carteData);
      }
    }
    
    localStorage.setItem('savedCartes', JSON.stringify(existingCartes));
    
    // Trigger custom event to notify other components
    window.dispatchEvent(new CustomEvent('cartesUpdated'));
  };

  const getTotalItems = () => {
    return Object.values(bookingData).flat().length + Object.values(staticItems).flat().length;
  };

  const getCartSummary = () => {
    const summary = {};
    Object.entries(bookingData).forEach(([key, items]) => {
      const totalCount = items.length + staticItems[key].length;
      if (totalCount > 0) {
        summary[key] = totalCount;
      }
    });
    return summary;
  };

  // Sample customer data
  const sampleCustomers = [
    {
      id: "CUST001",
      name: "John Doe",
      email: "john.doe@email.com",
      phone: "+1-555-0123",
      address: "123 Main St, New York, NY 10001"
    },
    {
      id: "CUST002",
      name: "Jane Smith",
      email: "jane.smith@email.com",
      phone: "+1-555-0124",
      address: "456 Oak Ave, Los Angeles, CA 90210"
    },
    {
      id: "CUST003",
      name: "Mike Johnson",
      email: "mike.johnson@email.com",
      phone: "+1-555-0125",
      address: "789 Pine Rd, Chicago, IL 60601"
    }
  ];

  const [customerData, setCustomerData] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const carteId = urlParams.get('carteId');
    
    if (carteId) {
      return {
        id: "CUST001",
        name: "",
        email: "",
        phone: "",
        address: ""
      };
    } else {
      // Generate unique ID for new carte
      const uniqueId = `CUST${Date.now().toString().slice(-3)}`;
      return {
        id: uniqueId,
        name: "",
        email: "",
        phone: "",
        address: ""
      };
    }
  });

  const [selectedCustomer, setSelectedCustomer] = useState("Select Employee");
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [cardsEnabled, setCardsEnabled] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);

  // Auto-save when booking data changes
  React.useEffect(() => {
    if (autoSaving) {
      const timer = setTimeout(() => {
        saveToLocalStorage();
        setAutoSaving(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [bookingData, autoSaving]);

  const handleCustomerEdit = (field, value) => {
    setCustomerData(prev => ({ ...prev, [field]: value }));
  };

  const handleCustomerSelect = (customerName) => {
    if (customerName === "Add New Customer") {
      setIsNewCustomer(true);
      setSelectedCustomer("Add New Customer");
      // Generate unique ID based on timestamp to avoid conflicts
      const uniqueId = `CUST${Date.now().toString().slice(-3)}`;
      setCustomerData({
        id: uniqueId,
        name: "",
        email: "",
        phone: "",
        address: ""
      });
      setCardsEnabled(true);
      // Clear booking data for new customer
      setBookingData({
        flight: [],
        hotel: [],
        railway: [],
        car: [],
        visa: []
      });
    } else {
      const customer = sampleCustomers.find(c => c.name === customerName);
      if (customer) {
        setIsNewCustomer(false);
        setSelectedCustomer(customerName);
        setCustomerData(customer);
        setCardsEnabled(true);
        
        // Load existing bookings for this customer
        const savedCartes = JSON.parse(localStorage.getItem('savedCartes') || '[]');
        const existingCarte = savedCartes.find(carte => carte.customer.id === customer.id);
        
        if (existingCarte) {
          setBookingData({
            flight: existingCarte.bookings.flight || [],
            hotel: existingCarte.bookings.hotel || [],
            railway: existingCarte.bookings.railway || [],
            car: existingCarte.bookings.car || [],
            visa: existingCarte.bookings.visa || []
          });
        } else {
          // Clear booking data if no existing carte
          setBookingData({
            flight: [],
            hotel: [],
            railway: [],
            car: [],
            visa: []
          });
        }
      }
    }
  };

 const bookingCards = [
  {
    id: "flight",
    title: "Flight Bookings",
    subtitle: "Domestic & International",
    icon: <FaPlane className="h-6 w-6" />,
    gradient: "bg-[#5a88c6] shadow-lg",
    items: [...bookingData.flight, ...staticItems.flight]
  },
  {
    id: "hotel",
    title: "Hotel Reservations",
    subtitle: "Luxury & Budget Options",
    icon: <FaHotel className="h-6 w-6" />,
    gradient: "bg-[#5a88c6] shadow-lg",
    items: [...bookingData.hotel, ...staticItems.hotel]
  },
  {
    id: "railway",
    title: "Train Services",
    subtitle: "Express & Local Routes",
    icon: <FaTrain className="h-6 w-6" />,
    gradient: "bg-[#5a88c6] shadow-lg",
    items: [...bookingData.railway, ...staticItems.railway]
  },
  {
    id: "car",
    title: "Vehicle Rentals",
    subtitle: "Premium & Economy Cars",
    icon: <FaCar className="h-6 w-6" />,
    gradient: "bg-[#5a88c6] shadow-lg",
    items: [...bookingData.car, ...staticItems.car]
  },
  {
    id: "visa",
    title: "Visa Services",
    subtitle: "Fast Track Processing",
    icon: <FaPassport className="h-6 w-6" />,
    gradient: "bg-[#5a88c6] shadow-lg",
    items: [...bookingData.visa, ...staticItems.visa]
  }
];



  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-navy-900 dark:via-navy-800 dark:to-navy-900 p-4">
      {/* Header Section */}
      <div className="mb-8">
        {/* <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            {new URLSearchParams(window.location.search).get('carteId') && (
              <button
                onClick={() => window.location.href = '/admin/carte-listing'}
                className="group flex items-center space-x-2 rounded-xl bg-white px-4 py-2 text-slate-600 shadow-lg hover:shadow-xl transition-all duration-300 hover:bg-slate-50 border border-slate-200"
              >
                <MdArrowBack className="h-5 w-5 group-hover:-translate-x-1 transition-transform" />
                <span className="font-medium">Back to Carte Listing</span>
              </button>
            )}
            <div className="flex items-center space-x-3">
              <div className="p-3 rounded-2xl bg-gradient-to-r from-indigo-500 to-purple-600 shadow-lg">
                <HiSparkles className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                  Travel Booking Manager
                </h1>
                <p className="text-slate-500 font-medium">Create and manage your travel arrangements</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className="text-sm text-slate-500">Total Items</p>
              <p className="text-2xl font-bold text-slate-800">{getTotalItems()}</p>
            </div>
            {autoSaving && (
              <div className="flex items-center space-x-2 bg-green-100 px-4 py-2 rounded-xl">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-green-500 border-t-transparent"></div>
                <span className="text-green-700 text-sm font-medium">Auto-saving...</span>
              </div>
            )}
          </div>
        </div> */}
        
        {/* Customer Information Card */}
        <Card extra="p-6 bg-white/70 dark:bg-navy-800/70 backdrop-blur-sm border border-white/20 dark:border-navy-700/50 shadow-xl">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600">
                <FaUser className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-slate-800 dark:text-white">Customer Information</h2>
            </div>
            {!editingCustomer ? (
              <button
                onClick={() => setEditingCustomer(true)}
                className="flex items-center space-x-2 rounded-xl bg-slate-100 dark:bg-navy-700 px-4 py-2 text-slate-600 dark:text-white hover:bg-slate-200 dark:hover:bg-navy-600 transition-colors"
              >
                <MdEdit className="h-4 w-4" />
                <span>Edit</span>
              </button>
            ) : (
              <button
                onClick={() => setEditingCustomer(false)}
                className="flex items-center space-x-2 rounded-xl bg-green-100 dark:bg-green-800 px-4 py-2 text-green-700 dark:text-green-200 hover:bg-green-200 dark:hover:bg-green-700 transition-colors"
              >
                <MdSave className="h-4 w-4" />
                <span>Save</span>
              </button>
            )}
          </div>
          
          <div className="space-y-4">
            <div className="relative z-[9999]">
              <label className="block text-sm font-semibold text-slate-700 dark:text-white mb-2">Select Employee</label>
              <Dropdown
                button={
                  <div className="flex h-10 w-full items-center justify-between rounded-lg border border-slate-200 dark:border-navy-600 bg-white dark:bg-navy-800 p-2 text-sm outline-none hover:border-slate-300 dark:hover:border-navy-500 cursor-pointer transition-colors">
                    <span className={selectedCustomer === "Select Employee" ? "text-slate-400 dark:text-slate-500" : "text-slate-700 dark:text-white font-medium"}>
                      {selectedCustomer === "Select Employee" ? "Choose employee..." : selectedCustomer}
                    </span>
                    <MdKeyboardArrowDown className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                  </div>
                }
                children={
                  <div className="bg-white dark:bg-navy-800 border border-slate-200 dark:border-navy-600 rounded-lg shadow-xl z-[9999]">
                    <button
                      onClick={() => handleCustomerSelect("Add New Customer")}
                      className="w-full flex items-center gap-2 px-3 py-2 text-slate-600 dark:text-white hover:bg-slate-50 dark:hover:bg-navy-700 transition-colors border-b border-slate-100 dark:border-navy-600"
                    >
                      <MdAdd className="h-4 w-4 text-brand-500" />
                      <span className="text-sm font-medium text-brand-500">Add New Customer</span>
                    </button>
                    {sampleCustomers.map((customer) => (
                      <button
                        key={customer.id}
                        onClick={() => handleCustomerSelect(customer.name)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-slate-600 dark:text-white hover:bg-slate-50 dark:hover:bg-navy-700 transition-colors"
                      >
                        <div className="h-6 w-6 rounded-full bg-brand-500 flex items-center justify-center text-white font-bold text-xs">
                          {customer.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <span className="text-sm">{customer.name}</span>
                      </button>
                    ))}
                  </div>
                }
                classNames="py-1 top-11 left-0 w-max z-[9999]"
              />
            </div>
            
            {isNewCustomer ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <input
                  type="text"
                  placeholder="Customer Name"
                  value={customerData.name}
                  onChange={(e) => handleCustomerEdit('name', e.target.value)}
                  className="px-3 py-2 border border-slate-200 dark:border-navy-600 bg-white dark:bg-navy-800 text-slate-900 dark:text-white rounded-lg text-sm focus:border-brand-500 focus:outline-none"
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={customerData.email}
                  onChange={(e) => handleCustomerEdit('email', e.target.value)}
                  className="px-3 py-2 border border-slate-200 dark:border-navy-600 bg-white dark:bg-navy-800 text-slate-900 dark:text-white rounded-lg text-sm focus:border-brand-500 focus:outline-none"
                />
                <input
                  type="tel"
                  placeholder="Phone"
                  value={customerData.phone}
                  onChange={(e) => handleCustomerEdit('phone', e.target.value)}
                  className="px-3 py-2 border border-slate-200 dark:border-navy-600 bg-white dark:bg-navy-800 text-slate-900 dark:text-white rounded-lg text-sm focus:border-brand-500 focus:outline-none"
                />
                <input
                  type="text"
                  placeholder="Address"
                  value={customerData.address}
                  onChange={(e) => handleCustomerEdit('address', e.target.value)}
                  className="px-3 py-2 border border-slate-200 dark:border-navy-600 bg-white dark:bg-navy-800 text-slate-900 dark:text-white rounded-lg text-sm focus:border-brand-500 focus:outline-none"
                />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="flex items-center space-x-2 p-2 rounded-lg bg-slate-50 dark:bg-navy-700">
                  <MdPerson className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Name</p>
                    <p className="text-sm font-semibold text-slate-800 dark:text-white">{customerData.name || 'No name available'}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 p-2 rounded-lg bg-slate-50 dark:bg-navy-700">
                  <MdEmail className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Email</p>
                    <p className="text-sm font-semibold text-slate-800 dark:text-white">{customerData.email || 'No email available'}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 p-2 rounded-lg bg-slate-50 dark:bg-navy-700">
                  <MdPhone className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Phone</p>
                    <p className="text-sm font-semibold text-slate-800 dark:text-white">{customerData.phone || 'No phone available'}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 p-2 rounded-lg bg-slate-50 dark:bg-navy-700">
                  <MdLocationOn className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Address</p>
                    <p className="text-sm font-semibold text-slate-800 dark:text-white">{customerData.address || 'No address available'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Booking Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 relative z-10">
        {bookingCards.map((card) => {
          const isDisabled = !cardsEnabled;
          return (
          <div key={card.id} className={`bg-white border border-gray-200 shadow-xl transition-all duration-300 overflow-hidden rounded-[20px] relative ${
            isDisabled 
              ? 'opacity-50 cursor-not-allowed' 
              : 'hover:shadow-2xl hover:scale-[1.02]'
          }`}>
            {/* Card Header */}
            <div className={`${card.gradient} p-6 relative overflow-hidden`}>
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-12 -translate-x-12"></div>
              <div className="relative ">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-3 rounded-2xl bg-white/20 backdrop-blur-sm">
                      {card.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">{card.title}</h3>
                      <p className="text-white/80 text-sm">{card.subtitle}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => !isDisabled && toggleCard(card.id)}
                    className={`p-2 rounded-xl transition-colors ${
                      isDisabled 
                        ? 'bg-white/10 cursor-not-allowed' 
                        : 'bg-white/20 hover:bg-white/30'
                    }`}
                    disabled={isDisabled}
                  >
                    <MdExpandMore className="h-6 w-6 text-white" />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl font-bold text-white">{card.items.length}</span>
                    <span className="text-white/80 text-sm">items</span>
                  </div>
                  {card.id === 'visa' ? (
                    <button
                      onClick={() => !isDisabled && setModals(prev => ({ ...prev, [card.id]: true }))}
                      className={`group flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-200 ${
                        isDisabled 
                          ? 'bg-white/10 cursor-not-allowed' 
                          : 'bg-white/20 hover:bg-white/30 hover:scale-105'
                      }`}
                      disabled={isDisabled}
                    >
                      <MdAdd className={`h-5 w-5 text-white transition-transform ${
                        !isDisabled ? 'group-hover:scale-110' : ''
                      }`} />
                      <span className="text-white font-medium">Add New</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => !isDisabled && openSearchModal(card.id)}
                      className={`group flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-200 ${
                        isDisabled 
                          ? 'bg-white/10 cursor-not-allowed' 
                          : 'bg-white/20 hover:bg-white/30 hover:scale-105'
                      }`}
                      disabled={isDisabled}
                    >
                      <MdSearch className={`h-5 w-5 text-white transition-transform ${
                        !isDisabled ? 'group-hover:scale-110' : ''
                      }`} />
                      <span className="text-white font-medium">Search</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          );
        })}
      </div>
      
      {/* Full Screen Modal for Expanded Card */}
      {expandedCard && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end justify-center">
          <div className="w-full max-w-6xl bg-white/95 backdrop-blur-lg border-t border-white/20 shadow-2xl rounded-t-3xl overflow-hidden animate-slide-up">
            <div className={`${bookingCards.find(c => c.id === expandedCard)?.gradient} p-6`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="p-3 rounded-2xl bg-white/20 backdrop-blur-sm">
                    {bookingCards.find(c => c.id === expandedCard)?.icon}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">
                      {bookingCards.find(c => c.id === expandedCard)?.title}
                    </h3>
                    <p className="text-white/80">
                      {bookingCards.find(c => c.id === expandedCard)?.items.length} items available
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setExpandedCard(null)}
                  className="p-3 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
                >
                  <MdClose className="h-6 w-6 text-white" />
                </button>
              </div>
            </div>
            
            <div className="p-8 max-h-96 overflow-y-auto">
              {(() => {
                const currentCard = bookingCards.find(c => c.id === expandedCard);
                if (!currentCard || currentCard.items.length === 0) {
                  return (
                    <div className="text-center py-16">
                      <div className={`p-8 rounded-3xl ${currentCard?.gradient} inline-block mb-6 shadow-lg`}>
                        <div className="text-white text-4xl">{currentCard?.icon}</div>
                      </div>
                      <h4 className="text-2xl font-bold text-slate-800 mb-3">No {currentCard?.title.toLowerCase()} added yet</h4>
                      <p className="text-slate-500 mb-8 text-lg">Start by adding your first booking item</p>
                      {expandedCard === 'visa' ? (
                        <button
                          onClick={() => {
                            if (cardsEnabled) {
                              setModals(prev => ({ ...prev, [expandedCard]: true }));
                              setExpandedCard(null);
                            }
                          }}
                          className={`px-8 py-4 ${currentCard?.gradient} text-white rounded-2xl font-semibold transition-all duration-300 ${
                            cardsEnabled ? 'hover:shadow-xl hover:scale-105' : 'opacity-50 cursor-not-allowed'
                          }`}
                          disabled={!cardsEnabled}
                        >
                          Add New Item
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            if (cardsEnabled) {
                              openSearchModal(expandedCard);
                              setExpandedCard(null);
                            }
                          }}
                          className={`px-8 py-4 ${currentCard?.gradient} text-white rounded-2xl font-semibold transition-all duration-300 ${
                            cardsEnabled ? 'hover:shadow-xl hover:scale-105' : 'opacity-50 cursor-not-allowed'
                          }`}
                          disabled={!cardsEnabled}
                        >
                          Search Items
                        </button>
                      )}
                    </div>
                  );
                }
                
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {currentCard.items.map((item) => (
                      <div key={item.id} className="group p-6 rounded-3xl bg-gradient-to-br from-white to-slate-50 border-2 border-slate-200 hover:border-slate-300 hover:shadow-xl transition-all duration-300 hover:scale-105">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-4">
                              <div className={`p-3 rounded-2xl ${currentCard.gradient} shadow-lg`}>
                                {currentCard.icon}
                              </div>
                              <div>
                                <h4 className="font-bold text-slate-800 text-lg">{item.name}</h4>
                                {item.price && (
                                  <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm font-semibold mt-1 inline-block">
                                    {item.price}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            <div className="space-y-3">
                              {item.route && (
                                <div className="flex items-center space-x-3 p-3 rounded-xl bg-slate-100">
                                  <FaMapMarkerAlt className="h-4 w-4 text-slate-500" />
                                  <span className="text-slate-700 font-medium">{item.route}</span>
                                </div>
                              )}
                              {item.location && (
                                <div className="flex items-center space-x-3 p-3 rounded-xl bg-slate-100">
                                  <FaMapMarkerAlt className="h-4 w-4 text-slate-500" />
                                  <span className="text-slate-700 font-medium">{item.location}</span>
                                </div>
                              )}
                              {item.time && (
                                <div className="flex items-center space-x-3 p-3 rounded-xl bg-slate-100">
                                  <FaClock className="h-4 w-4 text-slate-500" />
                                  <span className="text-slate-700 font-medium">{item.time}</span>
                                </div>
                              )}
                              {item.departureDate && (
                                <div className="flex items-center space-x-3 p-3 rounded-xl bg-slate-100">
                                  <FaCalendarAlt className="h-4 w-4 text-slate-500" />
                                  <span className="text-slate-700 font-medium">{item.departureDate}</span>
                                </div>
                              )}
                              {item.checkIn && (
                                <div className="flex items-center space-x-3 p-3 rounded-xl bg-slate-100">
                                  <FaCalendarAlt className="h-4 w-4 text-slate-500" />
                                  <span className="text-slate-700 font-medium">Check-in: {item.checkIn}</span>
                                </div>
                              )}
                              {item.checkOut && (
                                <div className="flex items-center space-x-3 p-3 rounded-xl bg-slate-100">
                                  <FaCalendarAlt className="h-4 w-4 text-slate-500" />
                                  <span className="text-slate-700 font-medium">Check-out: {item.checkOut}</span>
                                </div>
                              )}
                              {item.type && (
                                <div className="flex items-center space-x-3 p-3 rounded-xl bg-slate-100">
                                  <span className="text-slate-500">🏷️</span>
                                  <span className="text-slate-700 font-medium">{item.type}</span>
                                </div>
                              )}
                              {item.capacity && (
                                <div className="flex items-center space-x-3 p-3 rounded-xl bg-slate-100">
                                  <span className="text-slate-500">👥</span>
                                  <span className="text-slate-700 font-medium">{item.capacity}</span>
                                </div>
                              )}
                              {item.processing && (
                                <div className="flex items-center space-x-3 p-3 rounded-xl bg-slate-100">
                                  <span className="text-slate-500">⏱️</span>
                                  <span className="text-slate-700 font-medium">{item.processing}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex flex-col space-y-2 opacity-0 group-hover:opacity-100 transition-opacity ml-4">
                            <button
                              onClick={() => {
                                openDeleteModal(expandedCard, item.id, item.name);
                                setExpandedCard(null);
                              }}
                              className="p-3 rounded-xl bg-red-100 text-red-600 hover:bg-red-200 transition-colors shadow-sm"
                            >
                              <MdDelete className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Search Modals */}
      <FlightSearchModal
        isOpen={searchModals.flight}
        onClose={() => closeSearchModal('flight')}
        onSearch={(searchData) => handleSearch('flight', searchData)}
      />
      <VehicleSearchModal
        isOpen={searchModals.car}
        onClose={() => closeSearchModal('car')}
        onSearch={(searchData) => handleSearch('car', searchData)}
      />
      <TrainSearchModal
        isOpen={searchModals.railway}
        onClose={() => closeSearchModal('railway')}
        onSearch={(searchData) => handleSearch('railway', searchData)}
      />
      <HotelSearchModal
        isOpen={searchModals.hotel}
        onClose={() => closeSearchModal('hotel')}
        onSearch={(searchData) => handleSearch('hotel', searchData)}
      />
      
      {/* Results Modals */}
      <FlightResultsModal
        isOpen={resultsModals.flight}
        onClose={() => closeResultsModal('flight')}
        onSelect={(item) => handleSelectResult('flight', item)}
        searchData={searchResults}
      />
      <VehicleResultsModal
        isOpen={resultsModals.car}
        onClose={() => closeResultsModal('car')}
        onSelect={(item) => handleSelectResult('car', item)}
        searchData={searchResults}
      />
      <TrainResultsModal
        isOpen={resultsModals.railway}
        onClose={() => closeResultsModal('railway')}
        onSelect={(item) => handleSelectResult('railway', item)}
        searchData={searchResults}
      />
      <HotelResultsModal
        isOpen={resultsModals.hotel}
        onClose={() => closeResultsModal('hotel')}
        onSelect={(item) => handleSelectResult('hotel', item)}
        searchData={searchResults}
      />
      
      {/* Original Add Modals for Visa */}
      <VisaModal
        isOpen={modals.visa}
        onClose={() => setModals(prev => ({ ...prev, visa: false }))}
        onSave={(item) => {
          addItem('visa', item);
          setModals(prev => ({ ...prev, visa: false }));
        }}
        editingItem={editingItem}
      />
      
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        itemName={deleteModal.itemName}
      />
      

      
      {/* Alert Modal */}
      {alertModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <Card extra="w-96">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 rounded-t-[20px]">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold text-white">{alertModal.title}</h3>
                <button onClick={() => setAlertModal({ isOpen: false, title: '', message: '' })} className="rounded-full bg-white/20 p-2 text-white hover:bg-white/30">
                  <MdClose className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <p className="text-slate-700 mb-6">{alertModal.message}</p>
              
              <button
                onClick={() => setAlertModal({ isOpen: false, title: '', message: '' })}
                className="w-full rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 py-3 px-6 text-white font-semibold hover:from-blue-600 hover:to-blue-700 transition-all duration-200"
              >
                OK
              </button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BookingDetails;